import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BLOG_POSTS, CATEGORIES } from "@/lib/blog";

export default function Blog() {
  const [cat, setCat] = useState("All");

  const posts = useMemo(() => {
    if (cat === "All") return BLOG_POSTS;
    return BLOG_POSTS.filter((p) => p.category === cat);
  }, [cat]);

  return (
    <main data-testid="blog-page" className="pt-32 pb-24 bg-[#F9F8F6] relative z-10">
      <Seo
        title="ZEBO Journal · Interior Design Guides & Case Studies (Kolkata)"
        description="Honest, jargon-free guides on modular kitchen pricing, materials, warranty and turnkey interior projects in Kolkata. Written by the ZEBO design team."
        path="/blog"
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4">The ZEBO Journal</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#143D63] leading-[0.98] max-w-4xl">
            Honest interior design writing.
          </h1>
          <p className="text-[#143D63]/70 mt-6 max-w-2xl text-base sm:text-lg">
            Real pricing. Real materials. Real Kolkata projects. No jargon, no upsell — just the stuff our clients tell us they wish they'd known before signing with anyone.
          </p>
        </Reveal>

        <div data-testid="blog-cat-filter" className="mt-10 flex flex-wrap gap-2 border-b border-[#143D63]/15 pb-4">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors ${
                cat === c ? "bg-[#143D63] text-[#F8C21C]" : "text-[#143D63]/70 hover:text-[#8B0000]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div data-testid="blog-grid" className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block bg-white border border-[#143D63]/15 hover:border-[#F8C21C] transition-colors h-full"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#143D63]/5">
                  <img src={p.hero} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="overline text-[#8B0000] text-[10px]">{p.category}</p>
                  <h2 className="font-serif text-2xl text-[#143D63] mt-2 leading-tight group-hover:text-[#8B0000] transition-colors">{p.title}</h2>
                  <p className="text-sm text-[#143D63]/70 mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between text-[11px] text-[#143D63]/60">
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.readMins} min read</span>
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold text-[#143D63] group-hover:text-[#8B0000] transition-colors">
                    Read article <ArrowUpRight size={13} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {posts.length === 0 && (
          <div className="py-24 text-center text-[#143D63]/50">
            <p className="font-serif text-2xl">No articles in this category yet.</p>
          </div>
        )}
      </div>
    </main>
  );
}
